import type { SubscriptionFilters, VpnKey } from '@/types'
import { countryName } from '@/utils/countries'

function protocolShort(type: string): string {
  const t = (type || '').toLowerCase()
  if (t.includes('anytls')) return 'anytls'
  if (t.includes('vless')) return 'vless'
  if (t.includes('trojan')) return 'trojan'
  if (t.includes('shadowsocks') || t === 'ss') return 'ss'
  if (t === 'hysteria2') return 'hy2'
  if (t.includes('tuic')) return 'tuic'
  if (t === 'ssh' || t.includes('vssh')) return 'ssh'
  return t
}

/** Семейство протокола для фильтров подписки (как ProtocolFamily на бэкенде). */
export function protocolFamily(type: string): string {
  const t = (type || '').toLowerCase()
  if (t.includes('anytls')) return 'anytls'
  if (t.includes('vless')) return 'vless'
  if (t === 'ssh' || t.includes('vssh')) return 'ssh'
  if (t.includes('tuic')) return 'tuic'
  if (t.includes('trojan')) return 'trojan'
  if (t.includes('shadowsocks') || t === 'ss') return 'ss'
  if (t === 'hysteria2') return 'hysteria2'
  return t
}

function securityShort(item: VpnKey): string {
  if (item.protocol_security) return item.protocol_security.toLowerCase()
  const t = (item.protocol_type || '').toLowerCase()
  if (t.includes('reality')) return 'reality'
  // plain vless (incl. xhttp) — tls в URI, как на бэкенде
  if (t.includes('tls') || t === 'hysteria2' || t.includes('tuic') || t === 'vless') return 'tls'
  return ''
}

function transportShort(item: VpnKey): string {
  const pt = (item.protocol_type || '').toLowerCase()
  if (pt.includes('anytls')) return 'tcp'
  if (pt === 'ssh' || pt.includes('vssh')) return 'tcp'
  const t = (item.protocol_transport || 'tcp').toLowerCase()
  if (pt === 'hysteria2' || pt.includes('tuic')) return 'udp'
  return t
}

/** Подпись «Ключ N» (глобальный сквозной номер), либо пусто. */
export function keyNumberLabel(item: VpnKey): string {
  return item.key_number && item.key_number > 0 ? `Ключ ${item.key_number}` : ''
}

/**
 * Условное ядро протокола для UI свободных ключей.
 * Позже заменим значением из xbot API (`bot`: singbt/xrbt/vssh).
 */
export function protocolCoreLabel(item: VpnKey): string {
  switch (protocolFamily(item.protocol_type)) {
    case 'anytls':
    case 'tuic':
      return 'sing-box'
    case 'vless':
      return 'xray'
    case 'ssh':
      return 'ssh'
    default:
      return ''
  }
}

/**
 * Формат протокола: «vless | xhttp | tls | 443».
 * Порт — текущий (динамический, если включён).
 */
export function protocolLabel(item: VpnKey): string {
  const parts: string[] = []
  const proto = protocolShort(item.protocol_type)
  if (proto) parts.push(proto)
  const transport = transportShort(item)
  if (transport) parts.push(transport)
  const security = securityShort(item)
  if (security) parts.push(security)
  const port = item.display_port || item.port
  if (port) parts.push(String(port))
  if (parts.length) return parts.join(' | ')
  return item.protocol_display_name
    || `${item.protocol_type}_${item.protocol_transport || 'tcp'}_${item.port}`.toUpperCase()
}

export function locationLabel(item: VpnKey, lang: 'ru' | 'en' = 'ru'): string {
  if (item.country_code) {
    const localized = countryName(item.country_code, lang, item.location)
    if (localized) return localized
  }
  if (item.display_location && item.display_location.length !== 2) {
    return item.display_location
  }
  return item.location
}
export function keyIdentifier(item: VpnKey): string {
  return item.group_key || `id:${item.id}`
}

function pluralDays(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 14) return `${n} дней`
  if (mod10 === 1) return `${n} день`
  if (mod10 >= 2 && mod10 <= 4) return `${n} дня`
  return `${n} дней`
}

export function expiryBadge(item: VpnKey): { className: string; text: string } {
  if (!item.expires_at) return { className: 'badge bg-secondary', text: '—' }
  const ts = new Date(item.expires_at).getTime()
  const days = Math.ceil((ts - Date.now()) / 86400000)
  if (item.is_expired || days <= 0) return { className: 'badge badge-danger', text: 'Истек' }
  if (days <= 5) return { className: 'badge badge-danger', text: pluralDays(days) }
  if (days <= 20) return { className: 'badge badge-warning', text: pluralDays(days) }
  return { className: 'badge badge-success', text: pluralDays(days) }
}

export function isXhttpKey(key: VpnKey): boolean {
  return (key.protocol_transport ?? '').toLowerCase() === 'xhttp'
}

export function isXtlsVisionKey(key: VpnKey): boolean {
  return (key.protocol_flow ?? '').toLowerCase() === 'xtls-rprx-vision'
}

export function isKeyExcludedBySubscriptionFilters(
  key: VpnKey,
  filters: SubscriptionFilters | null | undefined,
): boolean {
  if (!filters) return false
  if (filters.exclude_xhttp && isXhttpKey(key)) return true
  if (filters.exclude_xtls_vision && isXtlsVisionKey(key)) return true
  const family = protocolFamily(key.protocol_type)
  if (filters.exclude_vless && family === 'vless') return true
  if (filters.exclude_anytls && family === 'anytls') return true
  if (filters.exclude_tuic && family === 'tuic') return true
  if (filters.exclude_ssh && family === 'ssh') return true
  return false
}

/** Ключ попадает в /sublink только если включён тумблером и не отсеян глобальным фильтром. */
export function isKeyInSubscriptionLink(
  key: VpnKey,
  filters: SubscriptionFilters | null | undefined,
): boolean {
  if (key.include_in_sublink === false) return false
  if (isKeyExcludedBySubscriptionFilters(key, filters)) return false
  return true
}

export function isManualGroupExpired(group: VpnKey[]): boolean {
  return Boolean(groupHeaderKey(group)?.is_expired)
}

/** Ключ для шапки группы: never transit-only row. */
export function groupHeaderKey(keys: VpnKey[]): VpnKey | undefined {
  if (!keys.length) return undefined
  const mainRow = keys.find(k => !k.is_transit_key && !k.transit_server_id)
  if (mainRow) return mainRow
  const withMain = keys.find(k => k.main_server_id)
  if (withMain) return withMain
  return keys[0]
}

function manualGroupSortKey(group: VpnKey[]): string {
  const head = groupHeaderKey(group)
  const la = (head?.location || '').toLowerCase()
  const sn = (head?.server_name || '').toLowerCase()
  return `${la}\0${sn}`
}

export function groupManualKeys(keys: VpnKey[]): VpnKey[][] {
  const groups = new Map<string, VpnKey[]>()
  for (const key of keys) {
    const gk = key.group_key || `id:${key.id}`
    const bucket = groups.get(gk) || []
    bucket.push(key)
    groups.set(gk, bucket)
  }
  const out = [...groups.values()]
  for (const bucket of out) {
    bucket.sort((a, b) => {
      const aTransit = Boolean(a.transit_server_id)
      const bTransit = Boolean(b.transit_server_id)
      if (aTransit !== bTransit) return aTransit ? 1 : -1
      return a.id - b.id
    })
  }
  out.sort((a, b) => {
    const aExpired = isManualGroupExpired(a)
    const bExpired = isManualGroupExpired(b)
    if (aExpired !== bExpired) return aExpired ? 1 : -1
    return manualGroupSortKey(a).localeCompare(manualGroupSortKey(b))
  })
  return out
}
